import { ExternalLink, Mail, RefreshCw, ShieldAlert } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import { CYBER_SAFETY_SUPPORT, CYBER_SAFETY_ACCENT as C } from "@/data/cyberSafetyBooks";

const OFFICIAL_RESOURCES = [
  {
    name: "National Cyber Crime Reporting Portal",
    detail: "Report any cyber fraud, financial or otherwise, directly to Indian law enforcement.",
    url: "https://www.cybercrime.gov.in",
  },
  {
    name: "Cyber Crime Helpline — 1930",
    detail: "The national helpline number to call immediately for financial cyber fraud, to help freeze a fraudulent transaction quickly.",
    url: "https://www.cybercrime.gov.in",
  },
  {
    name: "Your bank or UPI app's official fraud helpline",
    detail: "Found on the back of your card, inside your bank's app, or on your bank's official website — never from a number sent to you in a message or call.",
    url: null,
  },
  {
    name: "Reserve Bank of India (RBI)",
    detail: "For official guidance on banking safety and consumer protection in digital payments.",
    url: "https://www.rbi.org.in",
  },
];

export default function CyberSafetyUpdates() {
  useTrackPageView("cyber_updates_view");
  useSEO({
    title: "Updates & Official Resources — LifeIQ Cyber Fraud & Scam Safety",
    description: "Current guidance and official reporting resources for LifeIQ's Cyber Fraud & Scam Safety series. Updated as procedures or regulations change.",
    canonicalPath: "/books/cyber-safety/updates",
  });

  return (
    <main>
      <section style={{ backgroundColor: C.navy }}>
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 pt-16 md:pt-24 pb-16 md:pb-20">
          <HeroFade delay={0}>
            <div className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.gold }}>
              <RefreshCw size={14} /> Updates & official resources
            </div>
          </HeroFade>
          <KineticTitle
            testId="cyber-updates-title"
            className="mt-4 font-serif-d text-[30px] sm:text-[44px] leading-[1.1] text-white"
            lines={["Staying current, not just correct", "on the day we published."]}
          />
          <HeroFade delay={0.2}>
            <p className="mt-6 text-[16px] text-white/80 leading-[1.7] max-w-2xl">
              Fraud tactics, app names, and official reporting procedures change over time. This page reflects our
              current, verified understanding as of the series' latest edition. It does not replace official
              guidance — for the most current reporting steps and regulations, always check directly with the
              official sources below.
            </p>
          </HeroFade>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-16 md:py-20">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] font-semibold" style={{ color: C.teal }}>Where to report, directly</div>
            <h2 className="mt-3 font-serif-d text-[26px] sm:text-3xl leading-[1.15]" style={{ color: C.navy }}>Official channels — verified independently, every time.</h2>
          </Reveal>
          <div className="mt-10 space-y-5" data-testid="cyber-updates-resource-list">
            {OFFICIAL_RESOURCES.map((r) => (
              <Reveal key={r.name} delay={0.03}>
                <div className="border rounded-sm p-5 sm:p-6" style={{ borderColor: `${C.navy}14` }}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="font-serif-d text-[17px]" style={{ color: C.navy }}>{r.name}</div>
                    {r.url && (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`cyber-updates-resource-link-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        className="shrink-0 inline-flex items-center gap-1.5 text-[12px] font-semibold uppercase tracking-wide"
                        style={{ color: C.teal }}
                      >
                        Visit <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                  <p className="mt-2 text-[14px] leading-[1.6]" style={{ color: C.textMuted }}>{r.detail}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section style={{ backgroundColor: C.cream }}>
        <div className="max-w-[900px] mx-auto px-5 sm:px-8 py-16 md:py-20">
          <Reveal>
            <div className="flex items-start gap-4 border rounded-sm p-6 sm:p-7 bg-white" style={{ borderColor: `${C.red}33` }}>
              <ShieldAlert size={22} className="shrink-0 mt-0.5" style={{ color: C.red }} />
              <div>
                <div className="font-serif-d text-[18px]" style={{ color: C.navy }}>We will never claim to guarantee protection.</div>
                <p className="mt-2 text-[14px] leading-[1.65]" style={{ color: C.textMuted }}>
                  No book, app or checklist can promise you will never be defrauded. This series exists to help you
                  recognise recurring patterns and act quickly — awareness reduces risk, it does not eliminate it.
                  If you notice anything in the books that appears out of date, please tell us.
                </p>
                <a
                  data-testid="cyber-updates-report-email"
                  href={`mailto:${CYBER_SAFETY_SUPPORT.email}?subject=Cyber Safety Series — outdated information`}
                  className="mt-4 inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide underline underline-offset-2"
                  style={{ color: C.navy }}
                >
                  <Mail size={14} /> {CYBER_SAFETY_SUPPORT.email}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
